import { useState, useRef, useEffect, useCallback } from 'react';
import { useChatHistory } from '@/hooks/useChatHistory';
import { apiClient, type ChatMessage as ApiChatMessage } from '@/api/client';
import { ChatMessage } from '@/components/chat/chat-utils';
import { webSocketClient } from '@/api/websocket';

/** Strip the context/system prompt prefix from saved user messages, keeping only the actual question. */
function stripContextPrefix(content: string): string {
  const marker = '**My Question:**';
  const idx = content.lastIndexOf(marker);
  if (idx === -1) return content;

  let question = content.substring(idx + marker.length).trim();
  // Remove the trailing "Please provide specific remediation..." boilerplate appended by the router
  const suffixIdx = question.indexOf('\n\nPlease provide specific remediation');
  if (suffixIdx !== -1) {
    question = question.substring(0, suffixIdx).trim();
  }
  return question;
}

interface UseChatSessionManagerOptions {
  contextType?: string;
  contextId?: string;
  /** Set to false to disable session management (default: true when context is provided) */
  enabled?: boolean;
  /** ChatGPT semantics: open with a blank chat instead of auto-loading the
   *  latest session, and only create a session lazily when the user actually
   *  sends a message (so page visits never spawn empty sessions). History
   *  stays browsable via the dropdown. */
  startBlank?: boolean;
}

/** Notify the router to clear its cached session ID so the next message uses the correct session. */
function notifySessionChanged() {
  webSocketClient.sendMessage({ type: 'session_changed' } as any);
}

export function useChatSessionManager(
  options: UseChatSessionManagerOptions,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
) {
  const enabled = options.enabled !== false && !!(options.contextType && options.contextId);

  const chatHistory = useChatHistory(
    enabled
      ? { contextType: options.contextType, contextId: options.contextId }
      : { enabled: false }
  );

  const [showHistory, setShowHistory] = useState(false);
  const historyRef = useRef<HTMLDivElement>(null);

  // Close history dropdown on click outside
  useEffect(() => {
    if (!showHistory) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (historyRef.current && !historyRef.current.contains(e.target as Node)) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHistory]);

  // Reset initialLoadDone when context changes (e.g., switching incidents)
  const initialLoadDone = useRef(false);
  const prevContextId = useRef(options.contextId);
  useEffect(() => {
    if (prevContextId.current !== options.contextId) {
      initialLoadDone.current = false;
      prevContextId.current = options.contextId;
    }
  }, [options.contextId]);

  // Auto-load or create session on mount (or context change). Skipped in
  // startBlank mode: the page opens on an empty chat and the session is
  // created lazily on the first send (see ensureSession).
  useEffect(() => {
    if (!enabled || options.startBlank || initialLoadDone.current || chatHistory.loading) return;
    initialLoadDone.current = true;

    const activeSessions = chatHistory.sessions.filter(s => !s.deleted_at);
    if (activeSessions.length > 0) {
      handleLoadSession(activeSessions[0].id);
    } else {
      createAndLoadSession();
    }
  }, [enabled, chatHistory.sessions, chatHistory.loading]);

  // Lazily create + load a session the moment it's actually needed (first
  // message send in startBlank mode). No-op when a session is already loaded.
  const ensureSessionInFlight = useRef<Promise<void> | null>(null);
  const ensureSession = useCallback(async (): Promise<void> => {
    if (!enabled || chatHistory.currentSessionId) return;
    if (!ensureSessionInFlight.current) {
      ensureSessionInFlight.current = (async () => {
        try {
          const newSession = await chatHistory.createNewSession();
          await apiClient.loadChatSession(newSession.id);
          notifySessionChanged();
        } catch (err) {
          console.error('Failed to lazily create session:', err);
        } finally {
          ensureSessionInFlight.current = null;
        }
      })();
    }
    return ensureSessionInFlight.current;
  }, [enabled, chatHistory.currentSessionId, chatHistory.createNewSession]);

  const createAndLoadSession = async () => {
    try {
      const newSession = await chatHistory.createNewSession();
      await apiClient.loadChatSession(newSession.id);
      notifySessionChanged();
    } catch (err) {
      console.error('Failed to create initial session:', err);
    }
  };

  const handleLoadSession = useCallback(async (sessionId: string): Promise<ChatMessage[]> => {
    try {
      const apiMessages = await chatHistory.loadSession(sessionId);
      notifySessionChanged();
      const converted: ChatMessage[] = apiMessages.map((msg: ApiChatMessage, idx: number) => ({
        id: msg.id || `restored-${idx}`,
        text: msg.role === 'user' ? stripContextPrefix(msg.content) : msg.content,
        sender: msg.role === 'user' ? 'user' as const : 'bot' as const,
        timestamp: msg.created_at ? new Date(msg.created_at).getTime() : Date.now(),
        thinking: msg.thinking_text ? { text: msg.thinking_text, steps: [], isExpanded: false } : undefined,
        yamlFixes: msg.yaml_fixes ? Object.values(msg.yaml_fixes).flat() : undefined,
        cloudFixes: msg.cloud_fixes ? Object.values(msg.cloud_fixes).flat() : undefined,
      }));
      setMessages(converted);
      setShowHistory(false);
      return converted;
    } catch (err) {
      console.error('Failed to load session:', err);
      return [];
    }
  }, [chatHistory.loadSession, setMessages]);

  const handleNewChat = useCallback(async () => {
    try {
      const newSession = await chatHistory.createNewSession();
      await apiClient.loadChatSession(newSession.id);
      notifySessionChanged();
      setMessages([]);
      setShowHistory(false);
    } catch (err) {
      console.error('Failed to create new chat:', err);
    }
  }, [chatHistory.createNewSession, setMessages]);

  const formatSessionDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }, []);

  return {
    enabled,
    chatHistory,
    showHistory,
    setShowHistory,
    historyRef,
    handleLoadSession,
    handleNewChat,
    formatSessionDate,
    ensureSession,
  };
}
